import { computed, ref, watch } from 'vue'
import { defineStore } from 'pinia'
import { hidManager } from '@/api/device/hid'
import {
  buildEqCurveCommands,
  buildEqLoginStatusCommand,
  buildEqModeCommand,
  buildEqSinglePointCommand,
  type EqDataPoint,
} from '@/api/device/commands'
import type { EqMode, EqPresetData } from '@/api/eq'
import {
  addEqPreset,
  getEqList,
  getLastEq,
  pinEqPreset,
  removeEqPreset,
  renameEqPreset,
  resetEqPreset,
  setLastEqPreset,
  updateEqPoint,
} from '@/api/eq'
import { useUserStore } from './user'
import { useDeviceStore } from './device'

export const EQ_DISPLAY_FREQUENCIES = [
  '20Hz', '60Hz', '120Hz', '250Hz', '500Hz', '800Hz', '1K',
  '2K', '4K', '6K', '8K', '10K', '12K', '20K',
] as const

const PROTOCOL_FREQUENCIES = [
  '25HZ', '50HZ', '75HZ', '150HZ', '300HZ', '600HZ', '1K',
  '2K', '3K', '6K', '8K', '10K', '12K', '16K',
]
const DEFAULT_Q = '0.75'
const MAX_CUSTOM_PRESETS = 20
const MODE_TO_HARDWARE: Record<EqMode, number> = { game: 0, movie: 1, music: 2 }
const HARDWARE_TO_MODE: EqMode[] = ['game', 'movie', 'music']

function makePreset(id: string, name: string, gains: number[], qValues: number[]): EqPresetData {
  return {
    id,
    name,
    datapoint: PROTOCOL_FREQUENCIES.map((freq, index) => ({
      fid: index + 1,
      freq,
      gain: gains[index] ?? 0,
      q: String(qValues[index] ?? DEFAULT_Q),
    })),
  }
}

// 内置值与 oldcode/js/eq_setting.js 完全一致。
const SYSTEM_PRESETS: Record<EqMode, EqPresetData[]> = {
  game: [
    makePreset('TacticalFPS', '战术竞技射击', [-5, -3, -1, 0, -4, 2, 6, 5, 4, 3, 2, 1, 0, 0], [1, 1.2, 1.4, .6, 1.8, 1.5, 1.8, 2, 2.5, 2, 1.5, 1.2, 1, .75]),
    makePreset('BattleRoyale', '大逃杀', [3.5, 4, 2, 3, -3.5, -1.5, 3, 4, 2, 4, 3, 2, 0, 0], [1.2, 1.4, 1.6, .7, 1.5, 1.2, 1.5, 1.8, 2, 2.2, 2, 1.5, 1, .75]),
    makePreset('MilitaryShooter', '军事射击', [3, 3, 1, 5, -4, 0, 2, 4, 3, 5, 4, 2, 0, -1], [1, 1.2, 1.4, .6, 1.8, 1.5, 1.8, 2, 2.5, 2, 1.5, 1.2, 1, .75]),
    makePreset('ImmersiveRPG', '沉浸式角色扮演', [3, 4, 3, 4, -1.5, 0, 2, 1, -1, 1, 3, 3, 2, 0], [1, 1.2, 1.4, .7, 1.5, 1.2, 1.5, 1.8, 2, 2, 1.5, 1.2, 1, .75]),
  ],
  music: [
    makePreset('Pop Mode', '流行歌曲', [4, 2, 0, 3, -4, -1, 4, 3, -1, 2, 1, 4, 1, 0], [1, 1.2, 1.4, .5, 1.8, 1.2, 1.5, 2, 2.5, 1.8, 1.5, 1.2, 1, .75]),
    makePreset('Bass+', '低音加强', [3.5, 6, 4, 5, -5, -3, 0, 1, -2, 0, 0, 3, 0, 0], [1.2, 1.4, 1.6, .6, 1.8, 1.5, 1.2, 1.5, 2, 1.5, 1.2, 1, 1, .75]),
    makePreset('Vocal Focus', '清爽人声', [-3, -2, -1, 1, -4, 0, 5, 6, 4, 2, 1, 3, 1, 0], [1, 1.2, 1.4, .5, 1.8, 1.5, 1.8, 2.2, 2.5, 1.8, 1.5, 1.5, 1, .75]),
    makePreset('DJ Mode', 'DJ专用', [3, 6, 3, 2, -5, -1, 1, 2, -1, 4, 3, 5, 2, 0], [1, 1.4, 1.6, .6, 1.8, 1.5, 1.2, 1.5, 2, 2, 1.8, 1.2, 1, .75]),
  ],
  movie: [
    makePreset('Theater Surround', '影视音效', [4, 5, 4, 3, -4, -1, 4, 3, 1, 2, 3, 4, 2, 0], [1.2, 1.5, 1.6, .6, 1.8, 1.2, 1.5, 1.8, 2, 1.8, 1.5, 1.2, 1, .75]),
    makePreset('Horror Atmosphere', '恐怖氛围', [3, 3, 0, -2, -4, -1, 1, 3, 2, 5, 6, 4, 3, 1], [1, 1.4, 1.5, .8, 1.8, 1.5, 1.5, 2.2, 2.5, 2, 2.5, 1.5, 1.2, .75]),
    makePreset('Sci-Fi Space', '科幻空间', [4, 5, 3, 2, -4, -3, 1, 2, 3, 4, 5, 5, 3, 1], [1.2, 1.5, 1.6, .6, 1.8, 1.5, 1.5, 1.8, 2, 2, 2.2, 1.5, 1.2, .75]),
  ],
}

const SYSTEM_PRESET_IDS: Record<EqMode, string[]> = {
  game: SYSTEM_PRESETS.game.map(item => item.id),
  music: SYSTEM_PRESETS.music.map(item => item.id),
  movie: SYSTEM_PRESETS.movie.map(item => item.id),
}

function clonePoints(points: EqDataPoint[]): EqDataPoint[] {
  return points.map(point => ({ ...point, gain: Number(point.gain) || 0, q: String(point.q || DEFAULT_Q) }))
}

function clonePresets(presets: EqPresetData[]): EqPresetData[] {
  return presets.map(preset => ({ ...preset, datapoint: clonePoints(preset.datapoint) }))
}

function createZeroCurve(template?: EqDataPoint[]): EqDataPoint[] {
  return PROTOCOL_FREQUENCIES.map((freq, index) => ({
    fid: index + 1,
    freq: template?.[index]?.freq || freq,
    gain: 0,
    q: template?.[index]?.q || DEFAULT_Q,
  }))
}

interface EqModeState {
  enabled: boolean
  currentPreset: string
  systemPresets: EqPresetData[]
  customPresets: EqPresetData[]
  lastPreset: string | null
  pinnedPreset: string | null
}

function createModeState(mode: EqMode): EqModeState {
  return {
    enabled: false,
    currentPreset: SYSTEM_PRESETS[mode][0].id,
    systemPresets: clonePresets(SYSTEM_PRESETS[mode]),
    customPresets: [],
    lastPreset: null,
    pinnedPreset: null,
  }
}

const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))

function showGlobalLightTip(message: string): void {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent<string>('app-light-tip', { detail: message }))
}

export const useEqStore = defineStore('eq', () => {
  const userStore = useUserStore()
  const deviceStore = useDeviceStore()
  userStore.loadFromStorage()
  const gameEq = ref<EqModeState>(createModeState('game'))
  const musicEq = ref<EqModeState>(createModeState('music'))
  const movieEq = ref<EqModeState>(createModeState('movie'))
  const activeMode = ref<EqMode>('game')
  const appliedMode = ref<EqMode | null>(null)
  const appliedPresetId = ref<string | null>(null)
  const remoteLoaded = ref<Record<EqMode, boolean>>({ game: false, music: false, movie: false })
  // 登录态设备连接/回联时，EQ 页面不能先渲染默认中间态再等待云端数据覆盖。
  // false 表示当前账号 + 当前连接方式仍在执行 getLastEq -> list 的首屏恢复链路。
  const displayReady = ref(true)
  // 场景备份不能在 getLastEq + 当前模式 list 尚未恢复时读取临时的默认 EQ。
  // 不影响 EQ 页面现有的快速显示策略，单独提供给场景首份快照使用。
  const sceneRestoreReady = ref(false)

  // -------------------- EQ 数据来源仲裁 --------------------
  // 登录态：模式 / 标签 / 14 段曲线以 getLastEq + 对应模式 list 为准，EQ 开关只取当前链路 0x80。
  // 未登录：EQ 开关 / 模式 / 标签全部以 0x80 为准。
  // 每次连接/回联只在首次有效状态完成后补发一次完整曲线，避免 2 秒轮询重复下发。
  //
  // 为了消除 add/delete/pin/saveAs 后服务器 list 的短暂最终一致性延迟，
  // 这里只缓存“服务器已经确认成功”的快照，而不是把任意本地 UI 状态当成云端真值。
  // 刷新时先显示该服务器确认快照，同时立即重新拉服务器；最终仍由服务器校正。
  const sourceGeneration = ref(0)
  const mutationVersion = ref<Record<EqMode, number>>({ game: 0, music: 0, movie: 0 })
  const selectionVersion = ref<Record<EqMode, number>>({ game: 0, music: 0, movie: 0 })
  const cloudGuardUntil = ref<Record<EqMode, number>>({ game: 0, music: 0, movie: 0 })
  const reconcileTimers: Partial<Record<EqMode, ReturnType<typeof setTimeout>>> = {}
  const CLOUD_GUARD_MS = 6000
  const CLOUD_CACHE_PREFIX = 'eqCloudSnapshot:v2:'
  const LAST_LINKTYPE_PREFIX = 'eqLastLinktype:v1:'
  // 删除接口是最终一致的：接口成功后 /list 可能短时间仍返回已删除标签。
  // 用“待确认删除 tombstone”只屏蔽用户明确删除过的 presetId，直到服务器 list 真正确认它消失。
  // 这不是新的数据源，只是对服务器删除最终一致性的短期保护，并按账号 + linktype 隔离。
  const PENDING_DELETE_PREFIX = 'eqPendingDeletes:v1:'
  const PENDING_DELETE_TTL_MS = 120_000
  const pendingDeleteIds = ref<Record<EqMode, Record<string, number>>>({ game: {}, music: {}, movie: {} })

  // 新增接口同样存在“写接口已经发出，但 list 短时间仍是旧列表”的窗口。
  // 如果这时刷新页面，旧 list 会把刚新增的标签暂时隐藏，用户再次点 + 就可能生成同名标签。
  // pending add 只保存“已经退出编辑并开始提交”的新增操作，按账号 + linktype 隔离；
  // list 真正确认该 presetId 后立即清除，不把它当成新的长期数据源。
  const PENDING_ADD_PREFIX = 'eqPendingAdds:v1:'
  const PENDING_ADD_TTL_MS = 120_000
  type PendingAddEntry = { expiresAt: number; preset: EqPresetData }
  const pendingAdds = ref<Record<EqMode, Record<string, PendingAddEntry>>>({ game: {}, music: {}, movie: {} })
  // 登录态 EQ 的恢复只由 getLastEq + 当前 linktype 对应字段决定。
  // true 时表示当前登录+连接上下文从未使用过 EQ：任何 list/0x80 都不能自动选中标签或打开 EQ。
  let suppressLoggedInAutoSelection = false
  let sourceContextKey = ''
  const apiDebounceTimers = ref<Record<string, ReturnType<typeof setTimeout>>>({})
  // 标签点击属于离散操作，不再用定时器延迟 setLast。
  // 用串行队列保证快速连续点击时，后一次写入一定在前一次之后落库，避免旧请求晚返回反覆盖最新标签。
  let lastPresetWriteQueue: Promise<void> = Promise.resolve()
  let lastPresetWriteSerial = 0
  let deviceQueue: Promise<void> = Promise.resolve()

  // “+” 新增出来的标签先作为本地草稿存在：只有退出行内编辑时才真正调用新增接口。
  // 这样点击 + 后 UI 可以立即出现，不需要等待网络；同时避免“先 add 默认名、再 rename”造成接口竞态。
  const draftPresetIds = new Set<string>()

  // 用户从 UI 主动切换 EQ 后，0x80 可能恰好回到“切换前”的旧状态。
  // 在硬件确认本次写入前短暂忽略旧回读，避免底部菜单/按钮先变新值、又闪回旧值。
  let pendingHardwareWrite: {
    enabled: boolean
    mode: EqMode
    presetId: string | null
    expiresAt: number
  } | null = null
  const EQ_WRITE_GUARD_MS = 5000

  function markPendingHardwareWrite(enabled: boolean, mode: EqMode, presetId: string | null): void {
    pendingHardwareWrite = { enabled, mode, presetId, expiresAt: Date.now() + EQ_WRITE_GUARD_MS }
  }

  // activeMode 表示用户当前正在浏览的页签，appliedMode 才表示硬件实际应用的模式。
  // 0x80 每 2 秒都会回读一次，因此只允许首次硬件同步初始化浏览页签，
  // 后续回读不能把用户刚切换到的 Music/Movie 强制拉回硬件当前模式。
  let hardwareStatusInitialized = false

  // 当前连接上下文最后一次收到的硬件 EQ 开关。登录态只采纳这个开关位，
  // 绝不使用 0x80 的 eqMode / eqTag 覆盖 getLastEq + list.lastPreset。
  let latestHardwareEqEnabled: boolean | null = null
  let latestHardwareEqGeneration = -1

  // 登录态服务器上下文是否已经确定，以及 getLastEq 确定的模式。
  // null + resolved=true 表示当前 linktype 首次使用（getLastEq 对应字段为空）。
  let loggedInRestoreResolved = false
  let loggedInRestoredMode: EqMode | null = null

  // 未登录每次连接只补发一次完整曲线；sourceGeneration 变化后才允许再次补发。
  let guestCurveRestoreGeneration = -1
  // 登录 + 有最后记录时，回联允许先用“服务器确认快照”快速恢复，
  // getLastEq + list 返回后再按最新服务器数据做最终校正。
  // 用签名去重，而不是只按 generation 去重：这样缓存曲线和服务器最终曲线不一致时允许再补发一次。
  let loggedInCurveRestoreSignature = ''

  function getModeState(mode: EqMode): EqModeState {
    if (mode === 'music') return musicEq.value
    if (mode === 'movie') return movieEq.value
    return gameEq.value
  }

  function presetsForMode(mode: EqMode): EqPresetData[] {
    const state = getModeState(mode)
    return [...state.systemPresets, ...state.customPresets]
  }

  function findCustomPresetLocation(presetId: string): { mode: EqMode; state: EqModeState; preset: EqPresetData } | null {
    for (const mode of ['game', 'music', 'movie'] as EqMode[]) {
      const state = getModeState(mode)
      const preset = state.customPresets.find(item => item.id === presetId)
      if (preset) return { mode, state, preset }
    }
    return null
  }

  function isDraftPreset(presetId: string): boolean {
    return draftPresetIds.has(presetId)
  }

  function markMutation(mode: EqMode): void {
    mutationVersion.value[mode] += 1
  }

  function markSelection(mode: EqMode): void {
    selectionVersion.value[mode] += 1
  }

  function clearReconcileTimer(mode: EqMode): void {
    const timer = reconcileTimers[mode]
    if (!timer) return
    clearTimeout(timer)
    delete reconcileTimers[mode]
  }

  function currentCloudCacheKey(linktype = getLinktype()): string | null {
    if (!isLoggedIn()) return null
    return `${CLOUD_CACHE_PREFIX}${encodeURIComponent(getUuid())}:${linktype}`
  }

  function lastLinktypeStorageKey(): string | null {
    if (!isLoggedIn()) return null
    return `${LAST_LINKTYPE_PREFIX}${encodeURIComponent(getUuid())}`
  }

  function rememberLinktype(linktype: string): void {
    const key = lastLinktypeStorageKey()
    if (!key || (linktype !== '0' && linktype !== '1')) return
    try {
      localStorage.setItem(key, linktype)
    } catch {
      // localStorage 不可用时只影响首屏缓存，不影响服务器恢复。
    }
  }

  function readRememberedLinktype(): string | null {
    const key = lastLinktypeStorageKey()
    if (!key) return null
    try {
      const value = localStorage.getItem(key)
      return value === '0' || value === '1' ? value : null
    } catch {
      return null
    }
  }

  function currentPendingDeleteKey(): string | null {
    if (!isLoggedIn()) return null
    return `${PENDING_DELETE_PREFIX}${encodeURIComponent(getUuid())}:${getLinktype()}`
  }

  function currentPendingAddKey(): string | null {
    if (!isLoggedIn()) return null
    return `${PENDING_ADD_PREFIX}${encodeURIComponent(getUuid())}:${getLinktype()}`
  }

  function prunePendingDeletes(mode?: EqMode): void {
    const now = Date.now()
    const modes = mode ? [mode] : (['game', 'music', 'movie'] as EqMode[])
    for (const currentMode of modes) {
      const entries = pendingDeleteIds.value[currentMode]
      Object.keys(entries).forEach(id => {
        if (!Number.isFinite(entries[id]) || entries[id] <= now) delete entries[id]
      })
    }
  }

  function savePendingDeletes(): void {
    const key = currentPendingDeleteKey()
    if (!key) return
    prunePendingDeletes()
    try {
      const hasAny = (['game', 'music', 'movie'] as EqMode[]).some(mode => Object.keys(pendingDeleteIds.value[mode]).length)
      if (!hasAny) {
        localStorage.removeItem(key)
        return
      }
      localStorage.setItem(key, JSON.stringify({ version: 1, modes: pendingDeleteIds.value }))
    } catch (error) {
      console.warn('[EQ] 保存待确认删除状态失败:', error)
    }
  }

  function loadPendingDeletes(): void {
    pendingDeleteIds.value = { game: {}, music: {}, movie: {} }
    const key = currentPendingDeleteKey()
    if (!key) return
    try {
      const raw = localStorage.getItem(key)
      if (!raw) return
      const parsed = JSON.parse(raw)
      if (parsed?.version !== 1 || !parsed.modes) return
      for (const mode of ['game', 'music', 'movie'] as EqMode[]) {
        const source = parsed.modes[mode]
        if (source && typeof source === 'object') pendingDeleteIds.value[mode] = { ...source }
      }
      prunePendingDeletes()
      savePendingDeletes()
    } catch (error) {
      console.warn('[EQ] 读取待确认删除状态失败:', error)
    }
  }

  function addPendingDelete(mode: EqMode, presetId: string): void {
    pendingDeleteIds.value[mode][presetId] = Date.now() + PENDING_DELETE_TTL_MS
    savePendingDeletes()
  }

  function clearPendingDelete(mode: EqMode, presetId: string): void {
    if (!(presetId in pendingDeleteIds.value[mode])) return
    delete pendingDeleteIds.value[mode][presetId]
    savePendingDeletes()
  }

  function activePendingDeleteIds(mode: EqMode): Set<string> {
    prunePendingDeletes(mode)
    return new Set(Object.keys(pendingDeleteIds.value[mode]))
  }

  function prunePendingAdds(mode?: EqMode): void {
    const now = Date.now()
    const modes = mode ? [mode] : (['game', 'music', 'movie'] as EqMode[])
    for (const currentMode of modes) {
      const entries = pendingAdds.value[currentMode]
      Object.keys(entries).forEach(id => {
        const entry = entries[id]
        if (!entry || !Number.isFinite(entry.expiresAt) || entry.expiresAt <= now) delete entries[id]
      })
    }
  }

  function savePendingAdds(): void {
    const key = currentPendingAddKey()
    if (!key) return
    prunePendingAdds()
    try {
      const hasAny = (['game', 'music', 'movie'] as EqMode[]).some(mode => Object.keys(pendingAdds.value[mode]).length)
      if (!hasAny) {
        localStorage.removeItem(key)
        return
      }
      localStorage.setItem(key, JSON.stringify({ version: 1, modes: pendingAdds.value }))
    } catch (error) {
      console.warn('[EQ] 保存待确认新增状态失败:', error)
    }
  }

  function loadPendingAdds(): void {
    pendingAdds.value = { game: {}, music: {}, movie: {} }
    const key = currentPendingAddKey()
    if (!key) return
    try {
      const raw = localStorage.getItem(key)
      if (!raw) return
      const parsed = JSON.parse(raw)
      if (parsed?.version !== 1 || !parsed.modes) return
      for (const mode of ['game', 'music', 'movie'] as EqMode[]) {
        const source = parsed.modes[mode]
        if (!source || typeof source !== 'object') continue
        const next: Record<string, PendingAddEntry> = {}
        Object.entries(source as Record<string, PendingAddEntry>).forEach(([id, entry]) => {
          if (!entry?.preset) return
          next[id] = {
            expiresAt: Number(entry.expiresAt) || 0,
            preset: { ...normalizePreset(entry.preset), isCustom: true },
          }
        })
        pendingAdds.value[mode] = next
      }
      prunePendingAdds()
      savePendingAdds()
    } catch (error) {
      console.warn('[EQ] 读取待确认新增状态失败:', error)
    }
  }

  function addPendingAdd(mode: EqMode, preset: EqPresetData): void {
    pendingAdds.value[mode][preset.id] = {
      expiresAt: Date.now() + PENDING_ADD_TTL_MS,
      preset: { ...normalizePreset(preset), isCustom: true },
    }
    savePendingAdds()
  }

  function clearPendingAdd(mode: EqMode, presetId: string): void {
    if (!(presetId in pendingAdds.value[mode])) return
    delete pendingAdds.value[mode][presetId]
    savePendingAdds()
  }

  function activePendingAdds(mode: EqMode): PendingAddEntry[] {
    prunePendingAdds(mode)
    return Object.values(pendingAdds.value[mode])
  }

  function hydratePendingAddsIntoState(): void {
    for (const mode of ['game', 'music', 'movie'] as EqMode[]) {
      const entries = activePendingAdds(mode)
      if (!entries.length) continue
      const state = getModeState(mode)
      const existingIds = new Set(state.customPresets.map(item => item.id))
      const sorted = entries.slice().sort((a, b) => Number(a.preset.createdAt || 0) - Number(b.preset.createdAt || 0))
      sorted.forEach(entry => {
        if (!existingIds.has(entry.preset.id)) {
          state.customPresets.push({ ...normalizePreset(entry.preset), isCustom: true })
          existingIds.add(entry.preset.id)
        }
      })
      const latest = sorted[sorted.length - 1]?.preset
      if (latest && existingIds.has(latest.id)) state.currentPreset = latest.id
    }
  }

  function resumePendingDeletes(): void {
    if (!isLoggedIn()) return
    const uuid = getUuid()
    const linktype = getLinktype()
    for (const mode of ['game', 'music', 'movie'] as EqMode[]) {
      for (const presetId of activePendingDeleteIds(mode)) {
        // 页面刷新可能中断上一条 fetch。重新发送删除是幂等兜底；
        // tombstone 是否清除仍只由后续 list “确认该 id 已不存在”来决定。
        void removeEqPreset(mode, uuid, presetId, linktype)
          .then(() => scheduleRemoteReconcile(mode, 350))
          .catch(error => console.warn('[EQ] 恢复待确认删除请求失败:', mode, presetId, error))
      }
    }
  }

  function modeSnapshot(mode: EqMode) {
    const state = getModeState(mode)
    const safeCurrent = draftPresetIds.has(state.currentPreset)
      ? (state.lastPreset && !draftPresetIds.has(state.lastPreset) ? state.lastPreset : state.systemPresets[0]?.id || '')
      : state.currentPreset
    return {
      systemPresets: clonePresets(state.systemPresets),
      customPresets: clonePresets(state.customPresets.filter(preset => !draftPresetIds.has(preset.id))),
      currentPreset: safeCurrent,
      lastPreset: state.lastPreset && !draftPresetIds.has(state.lastPreset) ? state.lastPreset : null,
      pinnedPreset: state.pinnedPreset && !draftPresetIds.has(state.pinnedPreset) ? state.pinnedPreset : null,
    }
  }

  function readCloudSnapshot(linktype = getLinktype()): any | null {
    const key = currentCloudCacheKey(linktype)
    if (!key) return null
    try {
      const raw = localStorage.getItem(key)
      if (!raw) return null
      const parsed = JSON.parse(raw)
      return parsed && parsed.version === 2 ? parsed : null
    } catch (error) {
      console.warn('[EQ] 读取服务器快照失败:', error)
      return null
    }
  }

  function writeCloudModeSnapshot(
    mode: EqMode,
    options: { protect?: boolean; confirmActiveMode?: boolean } = {},
  ): void {
    const key = currentCloudCacheKey()
    if (!key) return
    try {
      const existing = readCloudSnapshot() || { version: 2, modes: {}, guardUntil: {} }
      if (options.protect) cloudGuardUntil.value[mode] = Date.now() + CLOUD_GUARD_MS
      existing.version = 2
      existing.updatedAt = Date.now()
      existing.modes = existing.modes || {}
      existing.modes[mode] = modeSnapshot(mode)
      existing.guardUntil = existing.guardUntil || {}
      existing.guardUntil[mode] = cloudGuardUntil.value[mode] || 0
      if (options.confirmActiveMode || !existing.activeMode) existing.activeMode = activeMode.value
      existing.appliedMode = appliedMode.value
      existing.appliedPresetId = appliedPresetId.value
      localStorage.setItem(key, JSON.stringify(existing))
    } catch (error) {
      console.warn('[EQ] 保存服务器快照失败:', error)
    }
  }

  function writeCloudContextSnapshot(restoredMode: EqMode | null): void {
    const key = currentCloudCacheKey()
    if (!key) return
    try {
      const existing = readCloudSnapshot() || { version: 2, modes: {}, guardUntil: {} }
      existing.version = 2
      existing.updatedAt = Date.now()
      existing.contextUsed = restoredMode !== null
      existing.restoredMode = restoredMode
      existing.activeMode = restoredMode || 'game'
      existing.appliedMode = restoredMode
      existing.appliedPresetId = restoredMode ? appliedPresetId.value : null
      localStorage.setItem(key, JSON.stringify(existing))
    } catch (error) {
      console.warn('[EQ] 保存连接方式恢复快照失败:', error)
    }
  }

  function hydrateCloudSnapshot(linktype = getLinktype()): boolean {
    if (!isLoggedIn()) return false
    const snapshot = readCloudSnapshot(linktype)
    if (!snapshot) return false

    if (['game', 'music', 'movie'].includes(snapshot.activeMode)) {
      activeMode.value = snapshot.activeMode
    }

    for (const mode of ['game', 'music', 'movie'] as EqMode[]) {
      const source = snapshot.modes?.[mode]
      if (!source) continue
      const state = getModeState(mode)
      if (Array.isArray(source.systemPresets) && source.systemPresets.length) {
        state.systemPresets = source.systemPresets.map(normalizePreset)
      }
      if (Array.isArray(source.customPresets)) {
        const pendingDeletes = activePendingDeleteIds(mode)
        state.customPresets = source.customPresets
          .filter((preset: EqPresetData) => !pendingDeletes.has(preset.id))
          .map((preset: EqPresetData, index: number) => ({
            ...normalizePreset(preset),
            isCustom: true,
            createdAt: preset.createdAt || Date.now() + index,
          }))
      }
      state.lastPreset = source.lastPreset || null
      state.pinnedPreset = source.pinnedPreset || null
      const candidate = source.currentPreset || source.lastPreset
      if (candidate && presetsForMode(mode).some(preset => preset.id === candidate)) {
        state.currentPreset = candidate
      }
      const guard = Number(snapshot.guardUntil?.[mode] || 0)
      cloudGuardUntil.value[mode] = Number.isFinite(guard) ? guard : 0
    }

    // 快照只用于“立即绘制上一份服务器已确认的模式/标签”，随后仍会后台 getLastEq + list 校正。
    // 登录态开关绝不能从服务器快照推断，只能使用当前连接 0x80。
    ;[gameEq.value, musicEq.value, movieEq.value].forEach(item => { item.enabled = false })
    appliedMode.value = null
    appliedPresetId.value = null
    const cachedMode = getModeFromLastEqField(snapshot.restoredMode)
    if (snapshot.contextUsed === false) {
      loggedInRestoreResolved = true
      loggedInRestoredMode = null
      ;[gameEq.value, musicEq.value, movieEq.value].forEach(item => { item.currentPreset = '' })
      activeMode.value = 'game'
    } else if (snapshot.contextUsed === true && cachedMode) {
      loggedInRestoreResolved = true
      loggedInRestoredMode = cachedMode
      activeMode.value = cachedMode
      const state = getModeState(cachedMode)
      const candidate = String(snapshot.appliedPresetId || state.currentPreset || state.lastPreset || '')
      if (candidate && presetsForMode(cachedMode).some(preset => preset.id === candidate)) {
        state.currentPreset = candidate
        const hardwareEnabled = latestHardwareEqGeneration === sourceGeneration.value
          ? latestHardwareEqEnabled === true
          : false
        state.enabled = hardwareEnabled
        appliedMode.value = hardwareEnabled ? cachedMode : null
        appliedPresetId.value = hardwareEnabled ? candidate : null
      }
    }
    return true
  }

  function cloudModeFingerprint(mode: EqMode): string | null {
    const snapshot = readCloudSnapshot()
    const source = snapshot?.modes?.[mode]
    if (!source) return null
    const compact = {
      system: (source.systemPresets || []).map((preset: EqPresetData) => ({
        id: preset.id,
        name: preset.name,
        datapoint: preset.datapoint?.map(point => [point.fid, point.freq, Number(point.gain) || 0, String(point.q || DEFAULT_Q)]),
      })),
      custom: (source.customPresets || []).map((preset: EqPresetData) => ({
        id: preset.id,
        name: preset.name,
        datapoint: preset.datapoint?.map(point => [point.fid, point.freq, Number(point.gain) || 0, String(point.q || DEFAULT_Q)]),
      })),
      lastPreset: source.lastPreset || null,
      defaultPreset: source.pinnedPreset || null,
    }
    return JSON.stringify(compact)
  }

  function remoteFingerprint(
    systemPresets: EqPresetData[],
    customPresets: EqPresetData[],
    lastPreset: string | null,
    pinnedPreset: string | null,
  ): string {
    return JSON.stringify({
      system: systemPresets.map(preset => ({
        id: preset.id,
        name: preset.name,
        datapoint: preset.datapoint.map(point => [point.fid, point.freq, Number(point.gain) || 0, String(point.q || DEFAULT_Q)]),
      })),
      custom: customPresets.map(preset => ({
        id: preset.id,
        name: preset.name,
        datapoint: preset.datapoint.map(point => [point.fid, point.freq, Number(point.gain) || 0, String(point.q || DEFAULT_Q)]),
      })),
      lastPreset: lastPreset || null,
      defaultPreset: pinnedPreset || null,
    })
  }

  function scheduleRemoteReconcile(mode: EqMode, delayMs = 650): void {
    clearReconcileTimer(mode)
    reconcileTimers[mode] = setTimeout(() => {
      delete reconcileTimers[mode]
      void loadFromRemote(mode, true)
    }, delayMs)
  }

  const currentModeState = computed(() => getModeState(activeMode.value))
  const currentSettings = currentModeState
  const allPresets = computed(() => presetsForMode(activeMode.value))
  const currentPreset = computed(() => allPresets.value.find(preset => preset.id === currentModeState.value.currentPreset))
  const currentPresetData = computed(() => currentPreset.value?.datapoint || createZeroCurve())
  const isSystemPreset = computed(() => (presetId: string) => SYSTEM_PRESET_IDS[activeMode.value].includes(presetId))
  const canAddMoreCustom = computed(() => currentModeState.value.customPresets.length < MAX_CUSTOM_PRESETS)
  const isEnabled = computed(() => appliedMode.value !== null && !!getModeState(appliedMode.value).enabled)
  const currentMode = computed(() => activeMode.value)
  const slidersInteractive = computed(() => (
    isEnabled.value
    && appliedMode.value === activeMode.value
    && appliedPresetId.value === currentModeState.value.currentPreset
  ))

  function isLoggedIn(): boolean {
    return userStore.isLoggedIn && !!userStore.uuid
  }

  function getUuid(): string {
    return userStore.uuid || ''
  }

  function getLinktype(): string {
    const connection = deviceStore.connectionType || hidManager.getConnectionType()
    return connection === '2.4G' ? '0' : '1'
  }

  function normalizePreset(preset: EqPresetData): EqPresetData {
    return { ...preset, datapoint: clonePoints(preset.datapoint) }
  }

  function enqueueDevice(task: () => Promise<void>): Promise<void> {
    deviceQueue = deviceQueue.then(task).catch(error => {
      console.error('[EQ] 设备命令执行失败:', error)
    })
    return deviceQueue
  }

  // 场景恢复 / 游客初始化：等待已经排队的 EQ 写操作完成。
  // 不能直接并发执行 0x72 和此前 0x52 曲线分包。
  function waitForDeviceQueue(): Promise<void> {
    return deviceQueue
  }

  async function sendLoginStatus(): Promise<void> {
    await hidManager.sendData(buildEqLoginStatusCommand(isLoggedIn()), false)
  }

  async function getPositioningProtocolState(): Promise<{ enabled: boolean; tag: number }> {
    try {
      const { useChijiStore } = await import('./chiji')
      const store = useChijiStore()
      const index = store.allPresets.findIndex(item => item.id === store.currentPresetId)
      return { enabled: store.enabled, tag: Math.max(0, index) }
    } catch {
      return { enabled: false, tag: 0 }
    }
  }

  async function sendModeState(enabled: boolean, mode: EqMode, presetId: string | null): Promise<void> {
    if (!hidManager.isConnected()) return
    const positioning = await getPositioningProtocolState()
    const tag = enabled && presetId
      ? Math.max(0, presetsForMode(mode).findIndex(preset => preset.id === presetId))
      : 0
    await sendLoginStatus()
    await hidManager.sendData(buildEqModeCommand(
      enabled,
      MODE_TO_HARDWARE[mode],
      tag,
      positioning.enabled,
      positioning.tag,
    ), false)
  }

  async function sendCurve(points: EqDataPoint[], includeLogin = true): Promise<void> {
    if (!hidManager.isConnected()) return
    if (includeLogin) await sendLoginStatus()
    const commands = buildEqCurveCommands(points)
    for (let index = 0; index < commands.length; index += 1) {
      await hidManager.sendData(commands[index], false)
      if (index < commands.length - 1) await delay(200)
    }
  }

  function setActiveMode(mode: EqMode) {
    activeMode.value = mode
    // UI 已经明确选择了浏览页签；即使首个 0x80 状态随后才到达，
    // 也只能更新硬件应用态，不能覆盖这次用户选择。
    hardwareStatusInitialized = true
  }

  const switchMode = setActiveMode

  async function applyPreset(
    mode: EqMode,
    presetId: string,
    options: { persist?: boolean; syncLastPreset?: boolean; markUserSelection?: boolean } = {},
  ): Promise<void> {
    const state = getModeState(mode)
    const preset = presetsForMode(mode).find(item => item.id === presetId)
    if (!preset) return
    // 首次使用上下文一旦由用户主动应用任意标签，就退出“禁止自动选中”状态。
    // 后续 setLast 会正常记录该连接方式自己的最后使用模式/标签。
    if (isLoggedIn()) suppressLoggedInAutoSelection = false
    const persist = options.persist !== false
    const syncLastPreset = options.syncLastPreset !== false
    // markUserSelection 保留为兼容原有调用参数；所有实际应用均保护当前选择。

    // 先一次性完成 UI 状态切换，再进行异步互斥/硬件下发。
    // 原先这里先 await 一键吃鸡状态，activeMode 已切换但目标 mode 仍是 disabled，
    // 会让底部 EQ 文案和下拉选中态出现一帧 OFF/未选中的闪烁。
    ;[gameEq.value, musicEq.value, movieEq.value].forEach(item => { item.enabled = false })
    activeMode.value = mode
    state.enabled = true
    state.currentPreset = presetId
    state.lastPreset = presetId
    appliedMode.value = mode
    appliedPresetId.value = presetId
    markPendingHardwareWrite(true, mode, presetId)
    // 场景应用同样属于明确的运行时 EQ 选择（虽然不写账号的 EQ setLast）。
    // 0x80 只允许更新开关，不能用进入页面时 getLastEq 恢复的旧模式覆盖它。
    // 更新本次连接的运行时目标，不修改后端最后使用模式；刷新仍由 getLastEq 校正。
    if (isLoggedIn()) {
      loggedInRestoreResolved = true
      loggedInRestoredMode = mode
      loggedInCurveRestoreSignature = buildLoggedInCurveRestoreSignature(
        sourceGeneration.value, mode, true, preset,
      )
    }
    // 即使来自场景（markUserSelection=false），也需要防止尚未完成的 /list 回读
    // 将刚选中的标签覆写成旧的 lastPreset，出现选中态/滑动条禁用。
    markSelection(mode)
    if (persist) saveToStorage()
    // UI 先立即切换；setLast 请求同一轮马上发出，不再额外等待 200ms。
    // Promise 在函数末尾等待，保证一次点击结束前已经完成“最后一次使用标签”的提交与确认。
    const lastPresetSync = syncLastPreset ? syncLastPresetNow(mode, presetId) : Promise.resolve()

    try {
      const { useChijiStore } = await import('./chiji')
      const chijiStore = useChijiStore()
      if (chijiStore.enabled) {
        await chijiStore.setEnabled(false)
        showGlobalLightTip('One-click positioning has been turned off.')
      }
    } catch {
      // 状态仓库可以在测试中独立使用。
    }

    await enqueueDevice(async () => {
      await sendModeState(true, mode, presetId)
      if (hidManager.isConnected()) await delay(30)
      await sendCurve(preset.datapoint, false)
    })
    await lastPresetSync
  }

  async function setEqEnabled(enabled: boolean): Promise<void> {
    if (enabled) {
      const state = currentModeState.value
      const fallbackPreset = state.currentPreset
        || (state.pinnedPreset && presetsForMode(activeMode.value).some(preset => preset.id === state.pinnedPreset) ? state.pinnedPreset : '')
        || state.systemPresets[0]?.id
        || state.customPresets[0]?.id
        || ''
      if (fallbackPreset) await applyPreset(activeMode.value, fallbackPreset)
      return
    }

    const mode = appliedMode.value || activeMode.value
    ;[gameEq.value, musicEq.value, movieEq.value].forEach(item => { item.enabled = false })
    appliedMode.value = null
    appliedPresetId.value = null
    markPendingHardwareWrite(false, mode, null)
    saveToStorage()

    await enqueueDevice(async () => {
      await sendModeState(false, mode, null)
      if (hidManager.isConnected()) await delay(30)
      await sendCurve(createZeroCurve(), false)
    })
  }

  async function selectPreset(presetId: string): Promise<void> {
    await applyPreset(activeMode.value, presetId)
  }

  async function updateEqGain(bandIndex: number, gain: number, skipDeviceSend = false): Promise<void> {
    const preset = currentPreset.value
    const point = preset?.datapoint[bandIndex]
    if (!preset || !point || !slidersInteractive.value) return
    point.gain = Math.max(-12, Math.min(12, Math.round(gain * 2) / 2))
    if (isLoggedIn() && !isDraftPreset(preset.id)) markMutation(activeMode.value)

    if (!skipDeviceSend && hidManager.isConnected()) {
      const command = buildEqSinglePointCommand(bandIndex, point)
      void enqueueDevice(async () => {
        await sendLoginStatus()
        await hidManager.sendData(command, false)
      })
    }

    if (isLoggedIn() && !isDraftPreset(preset.id)) {
      debouncedSyncPointToApi(activeMode.value, preset.id, bandIndex, point)
    }
  }

  async function updateEqFrequency(bandIndex: number, frequency: string): Promise<void> {
    const preset = currentPreset.value
    const point = preset?.datapoint[bandIndex]
    if (!preset || !point || !slidersInteractive.value) return
    point.freq = frequency
    await updateEqGain(bandIndex, point.gain)
  }

  function debouncedSyncPointToApi(mode: EqMode, presetId: string, bandIndex: number, point: EqDataPoint) {
    const key = `${mode}-${presetId}-${bandIndex}`
    if (apiDebounceTimers.value[key]) clearTimeout(apiDebounceTimers.value[key])
    apiDebounceTimers.value[key] = setTimeout(async () => {
      const uuid = getUuid()
      if (!uuid) return
      try {
        const response = await updateEqPoint(mode, uuid, presetId, point, getLinktype())
        if (Number(response.code) === 1) {
          // 单点接口成功后，当前曲线已经是服务器确认的新值。
          // 更新服务器快照并短暂保护，避免紧接着的 list 仍返回旧曲线把它顶回去。
          writeCloudModeSnapshot(mode, { protect: true, confirmActiveMode: true })
          scheduleRemoteReconcile(mode)
        }
      } catch (error) {
        console.error('[EQ] 同步频点失败:', error)
      }
      delete apiDebounceTimers.value[key]
    }, 500)
  }

  async function confirmLastPresetReadBack(
    mode: EqMode,
    presetId: string,
    requestUuid: string,
    requestLinktype: string,
    requestGeneration: number,
    requestSerial: number,
  ): Promise<boolean> {
    // 后端 /list 存在短暂最终一致性：setLast 成功后的第一次 list 可能仍返回旧 lastPreset，
    // 第二次读取才刷新为最新值。这里主动完成两次轻量回读，用户只需点击一次标签。
    const retryDelays = [120, 260]
    for (const waitMs of retryDelays) {
      if (waitMs > 0) await delay(waitMs)
      if (
        !isLoggedIn()
        || requestGeneration !== sourceGeneration.value
        || requestSerial !== lastPresetWriteSerial
        || getUuid() !== requestUuid
        || getLinktype() !== requestLinktype
      ) return false

      try {
        const response = await getEqList(mode, requestUuid, requestLinktype)
        if (Number(response.code) === 1 && String(response.data?.lastPreset || '') === presetId) return true
      } catch (error) {
        console.warn('[EQ] 回读最后使用标签失败，继续后台校正:', error)
      }
    }
    return false
  }

  function syncLastPresetNow(mode: EqMode, presetId: string): Promise<void> {
    if (!isLoggedIn()) return Promise.resolve()

    const requestGeneration = sourceGeneration.value
    const requestUuid = getUuid()
    const requestLinktype = getLinktype()
    const requestSerial = ++lastPresetWriteSerial

    const writeTask = lastPresetWriteQueue
      .catch(() => undefined)
      .then(async () => {
        // 如果这条任务还没开始时用户已经点了更新的标签，旧任务直接跳过，减少无意义写入。
        if (
          !isLoggedIn()
          || requestGeneration !== sourceGeneration.value
          || requestSerial !== lastPresetWriteSerial
          || getUuid() !== requestUuid
          || getLinktype() !== requestLinktype
        ) return

        try {
          const response = await setLastEqPreset(mode, requestUuid, presetId, requestLinktype)
          if (Number(response.code) !== 1) {
            console.warn('[EQ] 保存上次预设返回失败:', response)
            return
          }

          // 请求返回期间若又点击了别的标签，只让最新一次点击更新本地 lastPreset。
          if (
            requestGeneration !== sourceGeneration.value
            || requestSerial !== lastPresetWriteSerial
            || getUuid() !== requestUuid
            || getLinktype() !== requestLinktype
          ) return

          const state = getModeState(mode)
          state.lastPreset = presetId
          writeCloudModeSnapshot(mode, { protect: true, confirmActiveMode: true })
          // setLast 已由服务器明确返回成功：此时“最后一次使用模式”也已经切到当前 mode。
          // 同步刷新连接上下文快照，避免设备关机/回联时先按旧 restoredMode 绘制上一上次模式，
          // 再等 getLastEq 返回后跳到真正的最后模式。
          writeCloudContextSnapshot(mode)

          const confirmed = await confirmLastPresetReadBack(
            mode,
            presetId,
            requestUuid,
            requestLinktype,
            requestGeneration,
            requestSerial,
          )

          // 回读确认后再让完整 list 做最终一致性校正；即使两次回读仍旧，
          // 也保留当前服务器写成功快照并稍后继续校正，不把旧 lastPreset 顶回页面。
          scheduleRemoteReconcile(mode, confirmed ? 220 : 900)
        } catch (error) {
          console.error('[EQ] 保存上次预设失败:', error)
        }
      })

    // 队列自身永远保持可继续执行；当前调用仍拿到本次 task，方便 applyPreset 等待完成。
    lastPresetWriteQueue = writeTask.catch(() => undefined)
    return writeTask
  }

  async function applyCurrentEq(): Promise<void> {
    const preset = currentPreset.value
    if (!preset) return
    await enqueueDevice(() => sendCurve(preset.datapoint))
  }

  function createCustomPresetDraft(name: string, datapoint: EqDataPoint[]): string | null {
    // 锁定点击新增时所在模式；草稿立即进入 UI，不等待后端。
    const mode = activeMode.value
    const state = getModeState(mode)
    if (state.customPresets.length >= MAX_CUSTOM_PRESETS) return null

    const presetId = `custom-${mode}-${Date.now()}`
    const newPreset: EqPresetData = {
      id: presetId,
      name: name.trim(),
      datapoint: clonePoints(datapoint),
      isCustom: true,
      createdAt: Date.now(),
    }

    state.customPresets.push(newPreset)
    draftPresetIds.add(presetId)

    // applyPreset 在第一个 await 之前就会完成选中态更新，因此标签会在本次点击内立即变蓝。
    // 草稿阶段不写 localStorage / setLast，避免临时 id 被保存或发给服务器。
    void applyPreset(mode, presetId, { persist: false, syncLastPreset: false })
    return presetId
  }

  async function finalizeCustomPresetDraft(presetId: string, name: string): Promise<boolean> {
    const location = findCustomPresetLocation(presetId)
    if (!location || !draftPresetIds.has(presetId)) return false

    const { mode, state, preset } = location
    const normalizedName = name.trim().slice(0, 20)
    if (!normalizedName) return false
    if (presetsForMode(mode).some(item => item.id !== presetId && item.name.trim().toLowerCase() === normalizedName.toLowerCase())) {
      return false
    }

    preset.name = normalizedName
    const oldId = preset.id
    markMutation(mode)

    if (isLoggedIn()) {
      // 新增、确认真实 presetId、setLast 必须固定在同一个账号 + linktype 上完成。
      // 避免请求过程中用户切换 USB/2.4G 后，把这个新增标签写到另一套连接上下文。
      const requestUuid = getUuid()
      const requestLinktype = getLinktype()

      // 在真正发请求前先记录待确认新增。刷新/关闭页面时，即使 list 暂时还是旧数据，
      // 新标签也不会凭空消失，更不会因为名字又变成可用而再次创建同名标签。
      addPendingAdd(mode, preset)
      try {
        const response = await addEqPreset(mode, requestUuid, preset, requestLinktype)
        // 与旧版接口语义保持一致：code === 1 就代表新增成功。
        // 部分环境的 addEqData 成功响应并不会返回 data.id；上一版把“没有 id”误判成失败，
        // 结果就是服务器已经创建成功、前端却连续弹 Error，并且再次操作时制造重复标签。
        if (Number(response.code) !== 1) {
          clearPendingAdd(mode, oldId)
          return false
        }

        const responseData = response.data as unknown
        let returnedId: string | null = null
        if (typeof responseData === 'string' || typeof responseData === 'number') {
          returnedId = String(responseData)
        } else if (responseData && typeof responseData === 'object') {
          const data = responseData as Record<string, unknown>
          const candidate = data.id ?? data.presetId ?? data.eqId
          if (typeof candidate === 'string' || typeof candidate === 'number') returnedId = String(candidate)
        }

        // addEqData 有些环境不会返回新标签 id。此时不能直接拿前端草稿 id 去调用 setLast，
        // 否则会出现“Custom 4 已应用，但后台 lastPreset 仍是 Custom 3”。
        // 先从当前 linktype 的 list 中按 id / 唯一名称确认服务器真实 id；list 若有短暂延迟则重试。
        let remoteId = returnedId || oldId
        if (!returnedId) {
          const retryDelays = [0, 180, 420, 800]
          for (const waitMs of retryDelays) {
            if (waitMs) await delay(waitMs)
            try {
              const listResponse = await getEqList(mode, requestUuid, requestLinktype)
              if (Number(listResponse.code) !== 1 || !listResponse.data?.presets) continue
              const exactId = listResponse.data.presets.find(item => String(item.id) === oldId)
              const exactName = listResponse.data.presets.find(item => (
                String(item.name || '').trim().toLowerCase() === normalizedName.toLowerCase()
              ))
              const confirmed = exactId || exactName
              if (confirmed?.id != null) {
                remoteId = String(confirmed.id)
                break
              }
            } catch (error) {
              console.warn('[EQ] 新增后确认服务器 presetId 失败，将继续重试:', error)
            }
          }
        }

        preset.id = remoteId
        draftPresetIds.delete(oldId)

        // 服务器真实 id 与草稿 id 不同时，同步迁移 pending add。
        if (remoteId !== oldId) clearPendingAdd(mode, oldId)
        addPendingAdd(mode, preset)

        // 把所有仍引用临时 id 的状态一次性替换掉，避免 UI/硬件状态断链。
        if (state.currentPreset === oldId) state.currentPreset = remoteId
        if (state.lastPreset === oldId) state.lastPreset = remoteId
        if (state.pinnedPreset === oldId) state.pinnedPreset = remoteId
        if (appliedMode.value === mode && appliedPresetId.value === oldId) appliedPresetId.value = remoteId
        if (pendingHardwareWrite?.mode === mode && pendingHardwareWrite.presetId === oldId) {
          pendingHardwareWrite.presetId = remoteId
        }

        // 新增按钮会自动应用新标签，因此新增成功后必须立即把这个“真实 id”记录为最后一次使用。
        // 不再走 200ms debounce；保证顺序严格为 add -> resolve id -> setLast -> list reconcile。
        if (appliedMode.value === mode && appliedPresetId.value === remoteId) {
          try {
            const lastResponse = await setLastEqPreset(mode, requestUuid, remoteId, requestLinktype)
            if (Number(lastResponse.code) === 1) {
              state.lastPreset = remoteId
              // 新增标签自动应用时同样要立即更新最后使用模式快照，
              // 否则下一次设备回联仍可能先显示旧模式。
              if (getUuid() === requestUuid && getLinktype() === requestLinktype) {
                writeCloudContextSnapshot(mode)
              }
            } else {
              console.warn('[EQ] 新增标签 setLast 返回失败:', lastResponse)
            }
          } catch (error) {
            console.error('[EQ] 新增标签同步 lastPreset 失败:', error)
          }
        }

        saveToStorage()
        // addEqData / setLast 已按顺序完成，再由 list 做最终一致性确认。
        writeCloudModeSnapshot(mode, { protect: true, confirmActiveMode: true })
        scheduleRemoteReconcile(mode)
        return true
      } catch (error) {
        // 正常在线情况下的明确网络异常需要撤销 pending；页面刷新/关闭导致 JS 上下文销毁时
        // 这段 catch 不会执行，pending 会保留下来并由下一页的服务器 list 继续确认。
        clearPendingAdd(mode, oldId)
        console.error('[EQ] 保存新增预设失败:', error)
        return false
      }
    }

    draftPresetIds.delete(oldId)
    saveToStorage()
    return true
  }

  async function addCustomPreset(name: string, datapoint: EqDataPoint[]): Promise<string | null> {
    // Save As / 对话框仍走“确认即保存”；内部复用同一套草稿保存逻辑，保证新增语义一致。
    if (currentModeState.value.customPresets.length >= MAX_CUSTOM_PRESETS) throw new Error('CUSTOM_PRESET_LIMIT')
    const presetId = createCustomPresetDraft(name, datapoint)
    if (!presetId) throw new Error('CUSTOM_PRESET_LIMIT')
    const success = await finalizeCustomPresetDraft(presetId, name)
    if (!success) {
      const location = findCustomPresetLocation(presetId)
      if (location) {
        const index = location.state.customPresets.findIndex(item => item.id === presetId)
        if (index >= 0) location.state.customPresets.splice(index, 1)
      }
      draftPresetIds.delete(presetId)
      throw new Error('ADD_FAILED')
    }
    return currentModeState.value.currentPreset
  }

  async function deleteCustomPreset(presetId: string): Promise<boolean> {
    const location = findCustomPresetLocation(presetId)
    if (!location) return false
    const { mode, state } = location
    const index = state.customPresets.findIndex(preset => preset.id === presetId)
    if (index < 0) return false

    const removedPreset = state.customPresets[index]
    const wasDraft = draftPresetIds.has(presetId)
    const previousCurrent = state.currentPreset
    const previousLast = state.lastPreset
    const previousPinned = state.pinnedPreset
    const previousAppliedMode = appliedMode.value
    const previousAppliedPresetId = appliedPresetId.value

    // 视觉上立即删除，不等待 deleteEqData。
    state.customPresets.splice(index, 1)
    draftPresetIds.delete(presetId)
    if (state.lastPreset === presetId) state.lastPreset = null
    if (state.pinnedPreset === presetId) state.pinnedPreset = null

    // 删除当前标签后不要再跳回第一个系统预设。优先应用“最新的剩余自定义标签”
    // （customPresets 的尾项就是当前新增顺序中的最新标签）；没有自定义标签时再回退系统预设。
    const latestRemainingPreset = state.customPresets[state.customPresets.length - 1]
      || state.systemPresets[state.systemPresets.length - 1]
      || state.systemPresets[0]
    const fallback = latestRemainingPreset?.id || ''
    if (state.currentPreset === presetId) state.currentPreset = fallback
    markMutation(mode)
    saveToStorage()
    if (!wasDraft && isLoggedIn()) {
      // 如果这是刚新增、服务器 list 尚未确认的标签，删除后必须同时撤销 pending add，
      // 否则下一次 list 合并会把用户刚删掉的标签重新补回来。
      clearPendingAdd(mode, presetId)
      addPendingDelete(mode, presetId)
    }

    // 如果当前硬件正在使用被删标签，立即应用上面选出的最新剩余标签；
    // 同时由 applyPreset 更新 current/last/applied，并同步 setLast 与设备曲线。
    if (previousAppliedMode === mode && previousAppliedPresetId === presetId && fallback) {
      void applyPreset(mode, fallback, { markUserSelection: false })
    }

    if (wasDraft || !isLoggedIn()) return true

    try {
      const response = await removeEqPreset(mode, getUuid(), presetId, getLinktype())
      if (Number(response.code) === 1) {
        // 删除接口确认成功：把“已删除”的当前列表写成服务器确认快照。
        writeCloudModeSnapshot(mode, { protect: true, confirmActiveMode: true })
        scheduleRemoteReconcile(mode)
        return true
      }
    } catch (error) {
      console.error('[EQ] 删除预设失败:', error)
    }

    // 服务器明确失败才回滚，并撤销待确认删除标记。
    clearPendingDelete(mode, presetId)
    const safeIndex = Math.max(0, Math.min(index, state.customPresets.length))
    state.customPresets.splice(safeIndex, 0, removedPreset)
    state.currentPreset = previousCurrent
    state.lastPreset = previousLast
    state.pinnedPreset = previousPinned
    appliedMode.value = previousAppliedMode
    appliedPresetId.value = previousAppliedPresetId
    saveToStorage()
    return false
  }

  async function renameCustomPreset(presetId: string, name: string): Promise<boolean> {
    if (draftPresetIds.has(presetId)) {
      return finalizeCustomPresetDraft(presetId, name)
    }

    const location = findCustomPresetLocation(presetId)
    if (!location) return false
    const { mode, preset } = location
    const normalizedName = name.trim().slice(0, 20)
    if (!normalizedName) return false
    if (presetsForMode(mode).some(item => item.id !== presetId && item.name.toLowerCase() === normalizedName.toLowerCase())) {
      return false
    }
    markMutation(mode)
    if (isLoggedIn()) {
      try {
        const response = await renameEqPreset(mode, getUuid(), presetId, normalizedName, getLinktype())
        if (Number(response.code) !== 1) return false
      } catch (error) {
        console.error('[EQ] 重命名预设失败:', error)
        return false
      }
    }
    preset.name = normalizedName
    saveToStorage()
    if (isLoggedIn()) {
      writeCloudModeSnapshot(mode, { protect: true, confirmActiveMode: true })
      scheduleRemoteReconcile(mode)
    }
    return true
  }

  async function resetCurrentPreset(): Promise<boolean> {
    const preset = currentPreset.value
    if (!preset || preset.isCustom) return false
    const source = SYSTEM_PRESETS[activeMode.value].find(item => item.id === preset.id)
    if (!source) return false
    if (isLoggedIn()) {
      try {
        const response = await resetEqPreset(activeMode.value, getUuid(), preset.id, getLinktype())
        if (Number(response.code) !== 1) return false
      } catch (error) {
        console.error('[EQ] 重置预设失败:', error)
        return false
      }
    }
    preset.datapoint = clonePoints(source.datapoint)
    markMutation(activeMode.value)
    if (isLoggedIn()) {
      writeCloudModeSnapshot(activeMode.value, { protect: true, confirmActiveMode: true })
      scheduleRemoteReconcile(activeMode.value)
    }
    if (slidersInteractive.value) await applyCurrentEq()
    return true
  }

  async function pinPreset(presetId: string): Promise<boolean> {
    // UI 先立即排序；服务器失败才恢复。
    const mode = activeMode.value
    const state = getModeState(mode)
    if (!presetsForMode(mode).some(preset => preset.id === presetId)) return false

    const previousPinned = state.pinnedPreset
    state.pinnedPreset = presetId
    markMutation(mode)
    saveToStorage()

    if (isLoggedIn()) {
      try {
        const response = await pinEqPreset(mode, getUuid(), presetId, getLinktype())
        if (Number(response.code) !== 1) {
          state.pinnedPreset = previousPinned
          saveToStorage()
          return false
        }
        writeCloudModeSnapshot(mode, { protect: true, confirmActiveMode: true })
        scheduleRemoteReconcile(mode)
      } catch (error) {
        console.error('[EQ] 置顶预设失败:', error)
        state.pinnedPreset = previousPinned
        saveToStorage()
        return false
      }
    }

    return true
  }

  async function loadFromRemote(mode: EqMode, force = false): Promise<boolean> {
    if (!isLoggedIn() || (!force && remoteLoaded.value[mode])) return false

    const state = getModeState(mode)
    const requestGeneration = sourceGeneration.value
    const requestMutationVersion = mutationVersion.value[mode]
    const requestSelectionVersion = selectionVersion.value[mode]

    try {
      const response = await getEqList(mode, getUuid(), getLinktype())

      // 登录账号 / USB-2.4G 在请求期间发生变化：旧响应直接丢弃。
      if (requestGeneration !== sourceGeneration.value || !isLoggedIn()) return false
      if (Number(response.code) !== 1 || !response.data) return false

      const presets = response.data.presets.map(normalizePreset)
      const remoteSystem = presets.filter(preset => SYSTEM_PRESET_IDS[mode].includes(preset.id))
      const rawRemoteCustom = presets
        .filter(preset => !SYSTEM_PRESET_IDS[mode].includes(preset.id))
        .map((preset, index) => ({
          ...preset,
          isCustom: true,
          createdAt: preset.createdAt || Date.now() + index,
        }))

      // 删除接口返回成功后，服务器 list 可能还会短暂返回被删标签。
      // 只过滤当前账号/连接上下文中“用户明确删除且尚未被 list 确认消失”的 id。
      // 一旦 list 里真正看不到该 id，就清除 tombstone，后续完全恢复服务器原始数据。
      const pendingDeletes = activePendingDeleteIds(mode)
      const rawRemoteIds = new Set(rawRemoteCustom.map(preset => preset.id))
      let serverStillHasPendingDelete = false
      for (const presetId of pendingDeletes) {
        if (rawRemoteIds.has(presetId)) serverStillHasPendingDelete = true
        else clearPendingDelete(mode, presetId)
      }
      const activeDeletes = activePendingDeleteIds(mode)
      const remoteCustom = rawRemoteCustom.filter(preset => !activeDeletes.has(preset.id))

      // 新增写接口可能已经发出/成功，但 list 的最终一致性稍慢。
      // pending add 在这种窗口内继续显示，且会参与 nextCustomName，避免刷新后再次生成同名 Custom N。
      // 一旦服务器 list 真正包含同一个 presetId，立即清除 pending 标记。
      const rawRemoteIdsAfterDelete = new Set(remoteCustom.map(preset => preset.id))
      let serverStillMissingPendingAdd = false
      for (const entry of activePendingAdds(mode)) {
        const pendingPreset = entry.preset
        if (rawRemoteIdsAfterDelete.has(pendingPreset.id)) {
          clearPendingAdd(mode, pendingPreset.id)
          continue
        }
        if (activeDeletes.has(pendingPreset.id)) continue
        serverStillMissingPendingAdd = true
        remoteCustom.push({ ...normalizePreset(pendingPreset), isCustom: true, createdAt: pendingPreset.createdAt || Date.now() })
        rawRemoteIdsAfterDelete.add(pendingPreset.id)
      }

      // 请求发出后如果用户做了新增/删除/置顶/重命名等结构性操作，
      // 这次 list 很可能是操作前的旧快照，不能拿它把最新 UI 顶回去。
      if (mutationVersion.value[mode] !== requestMutationVersion) {
        remoteLoaded.value[mode] = false
        scheduleRemoteReconcile(mode)
        return false
      }

      // add/delete/pin/update 接口已经返回成功后，服务器 list 可能短时间仍是旧数据。
      // 当前缓存只保存“服务器已确认成功”的状态，所以保护窗口内若 list 与确认快照不一致，
      // 保留确认快照并继续后台重试；绝不让普通本地 UI 状态冒充服务器真值。
      const remoteFp = remoteFingerprint(
        remoteSystem.length ? remoteSystem : state.systemPresets,
        remoteCustom,
        response.data.lastPreset || null,
        response.data.defaultPreset || null,
      )
      const cachedFp = cloudModeFingerprint(mode)
      const guardUntil = Number(cloudGuardUntil.value[mode] || 0)
      if (cachedFp && Date.now() < guardUntil && cachedFp !== remoteFp) {
        remoteLoaded.value[mode] = false
        scheduleRemoteReconcile(mode, 700)
        return false
      }

      const preserveUserSelection = selectionVersion.value[mode] !== requestSelectionVersion
      const preservedCurrent = state.currentPreset
      const localDrafts = state.customPresets.filter(preset => draftPresetIds.has(preset.id))

      if (remoteSystem.length) state.systemPresets = remoteSystem
      state.customPresets = [...remoteCustom, ...localDrafts]
      state.lastPreset = response.data.lastPreset || null
      state.pinnedPreset = response.data.defaultPreset || null

      const allIds = new Set(presetsForMode(mode).map(preset => preset.id))
      if (preserveUserSelection && allIds.has(preservedCurrent)) {
        state.currentPreset = preservedCurrent
      } else if (isLoggedIn() && suppressLoggedInAutoSelection) {
        // 当前连接方式在 getLastEq 中为空/null，表示这套 EQ 从未使用过。
        // list 仍可正常拉取标签数据，但绝不能用 lastPreset/defaultPreset/首个标签自动制造选中态。
        state.currentPreset = ''
      } else if (state.lastPreset && allIds.has(state.lastPreset)) {
        state.currentPreset = state.lastPreset
      } else if (!allIds.has(state.currentPreset)) {
        const pinned = state.pinnedPreset && allIds.has(state.pinnedPreset) ? state.pinnedPreset : ''
        state.currentPreset = pinned || state.systemPresets[0]?.id || state.customPresets[0]?.id || ''
      }

      // 标签/曲线由服务器 list 校正后，如果该模式正处于实际应用态，
      // 同步应用标签引用；整个过程不读取硬件 eqMode/eqTag 作为登录态真值。
      if (appliedMode.value === mode && state.enabled && state.currentPreset) {
        appliedPresetId.value = state.currentPreset
      }

      cloudGuardUntil.value[mode] = 0
      remoteLoaded.value[mode] = true
      clearReconcileTimer(mode)
      // pending add 还没被 list 确认时，它只是“正在完成的写操作”，不能升级成服务器确认快照。
      // 等后续 list 真正包含该 id 后再写 cloud snapshot，保证登录态最终数据源仍然是服务器。
      if (!serverStillMissingPendingAdd) writeCloudModeSnapshot(mode)
      // 服务器仍返回某个已删除 id 时继续后台确认，但 UI 始终保持删除状态。
      if ((serverStillHasPendingDelete && activePendingDeleteIds(mode).size) || serverStillMissingPendingAdd) {
        scheduleRemoteReconcile(mode, 900)
      }
      return true
    } catch (error) {
      console.error(`[EQ] 加载 ${mode} 模式预设失败:`, error)
      return false
    }
  }

  async function loadAllFromRemote(force = false): Promise<void> {
    if (!isLoggedIn()) return
    await Promise.all([
      loadFromRemote('game', force),
      loadFromRemote('music', force),
      loadFromRemote('movie', force),
    ])
  }

  function queueGuestCurveRestore(enabled: boolean, preset: EqPresetData): void {
    const generation = sourceGeneration.value
    if (guestCurveRestoreGeneration === generation) return
    if (!deviceStore.isDeviceConnected || !hidManager.isConnected()) return

    // 先占用本连接 generation，避免下一次 0x80 在队列执行前再次排入同一套四包曲线。
    guestCurveRestoreGeneration = generation
    const curve = enabled ? clonePoints(preset.datapoint) : createZeroCurve(preset.datapoint)

    void enqueueDevice(async () => {
      if (generation !== sourceGeneration.value || isLoggedIn()) return
      if (!deviceStore.isDeviceConnected || !hidManager.isConnected()) return
      // 未登录只补发曲线，不反向发送 0x14，避免覆盖刚从 0x80 读到的开关/模式/标签。
      await sendCurve(curve)
    })
  }

  function buildLoggedInCurveRestoreSignature(
    generation: number,
    mode: EqMode,
    enabled: boolean,
    preset: EqPresetData,
  ): string {
    if (!enabled) return `${generation}|off`
    const points = preset.datapoint
      .map(point => `${point.fid}:${point.freq}:${Number(point.gain) || 0}:${String(point.q || DEFAULT_Q)}`)
      .join('|')
    return `${generation}|on|${mode}|${preset.id}|${points}`
  }

  // 已登录场景刷新时，硬件保留场景 EQ；不能先用 getLastEq 的旧曲线覆盖它，
  // 否则页面显示场景开启但耳机短暂变成场景开启前的 EQ。
  // 待 scenario store 读取 getCurrent 后按需同步当前场景的曲线。
  function isSavedSceneActiveForCurrentConnection(): boolean {
    if (!isLoggedIn()) return false
    const connection = deviceStore.connectionType
    if (connection !== 'USB' && connection !== '2.4G') return false
    try {
      return !!sessionStorage.getItem(`scenarioActive:v1:${getUuid()}:${connection}`)
    } catch { return false }
  }

  function queueLoggedInCurveRestore(mode: EqMode): void {
    if (isSavedSceneActiveForCurrentConnection()) return
    const generation = sourceGeneration.value
    if (!isLoggedIn() || !deviceStore.isDeviceConnected || !hidManager.isConnected()) return
    if (!loggedInRestoreResolved || loggedInRestoredMode !== mode || suppressLoggedInAutoSelection) return

    // 必须已经拿到“当前连接 generation”的 0x80 开关，不能把尚未回读误判成关闭。
    if (latestHardwareEqGeneration !== generation || latestHardwareEqEnabled === null) return

    const state = getModeState(mode)
    const preset = presetsForMode(mode).find(item => item.id === state.currentPreset)
    if (!preset) return

    const enabled = latestHardwareEqEnabled
    const signature = buildLoggedInCurveRestoreSignature(generation, mode, enabled, preset)
    if (loggedInCurveRestoreSignature === signature) return

    const curve = enabled ? clonePoints(preset.datapoint) : createZeroCurve(preset.datapoint)

    // 先记录本次排队签名，避免 2 秒轮询把同一条曲线重复排入。
    // 如果后台服务器校正后模式/标签/曲线发生变化，签名也会变化，因此允许再补发一次最终曲线。
    loggedInCurveRestoreSignature = signature
    void enqueueDevice(async () => {
      if (generation !== sourceGeneration.value || !isLoggedIn() || isSavedSceneActiveForCurrentConnection()) return
      if (!deviceStore.isDeviceConnected || !hidManager.isConnected()) return
      if (loggedInRestoredMode !== mode || suppressLoggedInAutoSelection
        || loggedInCurveRestoreSignature !== signature) return
      // 运行时用户/场景可能已切换 EQ，过期的恢复曲线不能再下发覆盖新曲线。
      // 登录有历史：0x80 只决定开关。开=>服务器最后标签曲线；关=>14 段全 0dB。
      await sendCurve(curve)
    })
  }

  function queueLoggedInFastZeroRestore(): void {
    if (isSavedSceneActiveForCurrentConnection()) return
    const generation = sourceGeneration.value
    if (!isLoggedIn() || !deviceStore.isDeviceConnected || !hidManager.isConnected()) return
    if (latestHardwareEqGeneration !== generation || latestHardwareEqEnabled !== false) return

    // 登录态只要当前连接 0x80 明确为关闭，最终设备曲线一定应为全 0dB。
    // 这一步不需要等待 getLastEq / list，也不会改变页面模式和标签。
    const signature = `${generation}|off`
    if (loggedInCurveRestoreSignature === signature) return
    loggedInCurveRestoreSignature = signature

    void enqueueDevice(async () => {
      if (generation !== sourceGeneration.value || !isLoggedIn() || isSavedSceneActiveForCurrentConnection()) return
      if (!deviceStore.isDeviceConnected || !hidManager.isConnected()) return
      if (latestHardwareEqGeneration !== generation || latestHardwareEqEnabled !== false
        || loggedInCurveRestoreSignature !== signature) return
      await sendCurve(createZeroCurve())
    })
  }

  function syncHardwareStatus(enabled: boolean, modeValue: number, presetIndex: number): void {
    const mode = HARDWARE_TO_MODE[Math.max(0, Math.min(2, modeValue))] || 'game'
    const state = getModeState(mode)
    const preset = presetsForMode(mode)[Math.max(0, presetIndex)] || state.systemPresets[0]

    if (pendingHardwareWrite) {
      const expected = pendingHardwareWrite
      // 登录态只信 0x80 的开关位，因此硬件写确认也只比较 enabled；
      // 未登录仍完整比较开关 + 模式 + 标签。
      const confirmed = isLoggedIn()
        ? enabled === expected.enabled
        : enabled === expected.enabled
          && (!enabled || (mode === expected.mode && preset.id === expected.presetId))

      if (confirmed) {
        pendingHardwareWrite = null
      } else if (Date.now() < expected.expiresAt) {
        return
      } else {
        pendingHardwareWrite = null
      }
    }

    if (isLoggedIn()) {
      // 登录 + 有历史：只从 0x80 更新开关；模式和标签继续由 getLastEq + list.lastPreset 决定。
      latestHardwareEqEnabled = enabled
      latestHardwareEqGeneration = sourceGeneration.value

      // 关闭态不依赖服务器模式/标签，先立即补发全 0dB；网络仍继续后台校正页面上下文。
      if (!enabled) queueLoggedInFastZeroRestore()

      // getLastEq 尚未确定当前 linktype，或当前 linktype 是首次使用时，不允许 0x80 制造模式/标签。
      if (!loggedInRestoreResolved || !loggedInRestoredMode || suppressLoggedInAutoSelection) return

      const restoredState = getModeState(loggedInRestoredMode)
      const restoredPreset = presetsForMode(loggedInRestoredMode)
        .find(item => item.id === restoredState.currentPreset)
      if (!restoredPreset) return

      ;[gameEq.value, musicEq.value, movieEq.value].forEach(item => { item.enabled = false })
      restoredState.enabled = enabled
      appliedMode.value = enabled ? loggedInRestoredMode : null
      appliedPresetId.value = enabled ? restoredPreset.id : null
      // 如果服务器模式/标签已经恢复完成，则当前 0x80 到达时触发本连接唯一一次曲线恢复。
      queueLoggedInCurveRestore(loggedInRestoredMode)
      return
    }

    // 未登录：页面状态完整以硬件保存的 eqEnabled / eqMode / eqTag 为准。
    ;[gameEq.value, musicEq.value, movieEq.value].forEach(item => { item.enabled = false })
    // activeMode 只表示用户当前浏览的页签。首次回读时用硬件模式初始化，
    // 后续轮询只同步实际应用状态，不能把刚点击的 Game / Music 拉回 Movie。
    if (!hardwareStatusInitialized) {
      activeMode.value = mode
      hardwareStatusInitialized = true
    }
    state.currentPreset = preset.id
    state.enabled = enabled
    appliedMode.value = enabled ? mode : null
    appliedPresetId.value = enabled ? preset.id : null

    // 本次连接首次有效 0x80：EQ 开则补发当前标签完整曲线，EQ 关则补发全 0dB。
    queueGuestCurveRestore(enabled, preset)
  }

  function syncLatestHardwareForCurrentContext(): void {
    const status = deviceStore.deviceStatus
    if (!status) return
    const useDongle = deviceStore.connectionType === '2.4G'
    const enabled = useDongle ? status.dongleEqEnabled : status.usbEqEnabled
    const mode = useDongle ? status.dongleEqMode : status.usbEqMode
    const tag = useDongle ? status.dongleEqTag : status.usbEqTag
    syncHardwareStatus(
      Number(enabled ?? status.eqEnabled ?? 0) === 1,
      Number(mode ?? status.eqMode ?? 0),
      Number(tag ?? status.eqTag ?? 0),
    )
  }

  function resetModeData(): void {
    gameEq.value = createModeState('game')
    musicEq.value = createModeState('music')
    movieEq.value = createModeState('movie')
    appliedMode.value = null
    appliedPresetId.value = null
    pendingHardwareWrite = null
    hardwareStatusInitialized = false
    latestHardwareEqEnabled = null
    latestHardwareEqGeneration = -1
    loggedInRestoreResolved = false
    loggedInRestoredMode = null
    guestCurveRestoreGeneration = -1
    loggedInCurveRestoreSignature = ''
    suppressLoggedInAutoSelection = false
    draftPresetIds.clear()
    remoteLoaded.value = { game: false, music: false, movie: false }
    mutationVersion.value = { game: 0, music: 0, movie: 0 }
    selectionVersion.value = { game: 0, music: 0, movie: 0 }
    cloudGuardUntil.value = { game: 0, music: 0, movie: 0 }
    // 账号 / USB-2.4G 上下文切换后，让旧的 setLast 队列结果全部失效。
    lastPresetWriteSerial += 1
    pendingDeleteIds.value = { game: {}, music: {}, movie: {} }
    pendingAdds.value = { game: {}, music: {}, movie: {} }
    ;(['game', 'music', 'movie'] as EqMode[]).forEach(clearReconcileTimer)
  }

  function clearLoggedInSelection(): void {
    ;[gameEq.value, musicEq.value, movieEq.value].forEach(item => {
      item.enabled = false
      item.currentPreset = ''
    })
    activeMode.value = 'game'
    appliedMode.value = null
    appliedPresetId.value = null
  }

  function getModeFromLastEqField(value: unknown): EqMode | null {
    const normalized = String(value ?? '').trim().toLowerCase()
    if (normalized === 'game') return 'game'
    if (normalized === 'music') return 'music'
    // getLastEq 后端返回 cinema；本地服务器确认快照使用内部模式名 movie。
    // 两种值都要识别，否则电影模式快照在下一次回联时会被当成无效模式。
    if (normalized === 'cinema' || normalized === 'movie') return 'movie'
    return null
  }

  async function syncRestoredEqToHardware(mode: EqMode | null): Promise<void> {
    if (!hidManager.isConnected() || isSavedSceneActiveForCurrentConnection()) return

    if (!mode) {
      // 首次使用账号保持原逻辑：明确关闭 EQ，并写入全 0dB。
      await enqueueDevice(async () => {
        await sendModeState(false, 'game', null)
        if (hidManager.isConnected()) await delay(30)
        await sendCurve(createZeroCurve(), false)
      })
      return
    }

    // 登录 + 有最后记录：模式/标签来自 getLastEq + list.lastPreset；
    // 真正下发什么曲线必须等待当前连接 0x80 的开关：开=>最后标签完整 EQ，关=>14 段全 0dB。
    queueLoggedInCurveRestore(mode)
  }

  async function restoreLoggedInContext(requestGeneration: number): Promise<void> {
    if (!isLoggedIn()) return

    const uuid = getUuid()
    const linktype = getLinktype()

    try {
      // 必须是当前登录连接上下文的第一个 EQ 网络请求。
      // 请求体只有 uuid；USB / 2.4G 的分流只在读取返回字段时发生。
      const response = await getLastEq(uuid)
      if (requestGeneration !== sourceGeneration.value || !isLoggedIn()) return
      if (Number(response.code) !== 1) {
        console.warn('[EQ] getLastEq 返回失败，保留上一份服务器确认快照，等待下次恢复。', response)
        return
      }

      const rawMode = linktype === '0'
        ? response.data?.linktype0
        : response.data?.linktype1
      const restoredMode = getModeFromLastEqField(rawMode)

      // 到这里之后才允许其他 EQ API 请求，确保顺序始终是 getLastEq -> list。
      loadPendingDeletes()
      loadPendingAdds()

      if (!restoredMode) {
        // 当前连接方式对应字段为 "" / null（或 data=null）：仅代表这一套 EQ 首次使用。
        // 首次使用保持原逻辑，0x80 也不能把页面重新打开或制造标签。
        loggedInRestoreResolved = true
        loggedInRestoredMode = null
        suppressLoggedInAutoSelection = true
        clearLoggedInSelection()

        // 首屏只等待默认 Game 的 list：它决定当前页面要展示的标签。
        // Music / Movie 改为首屏完成后后台补拉，避免为了不可见页签多等两次网络请求。
        const gameLoaded = await loadFromRemote('game', true)
        if (requestGeneration !== sourceGeneration.value || !isLoggedIn()) return
        if (!gameLoaded) return
        clearLoggedInSelection()
        writeCloudContextSnapshot(null)
        sceneRestoreReady.value = true
        void Promise.all((['music', 'movie'] as EqMode[]).map(mode => loadFromRemote(mode, true)))
        void syncRestoredEqToHardware(null)
        return
      }

      suppressLoggedInAutoSelection = false
      loggedInRestoreResolved = true
      loggedInRestoredMode = restoredMode
      // 页面已用当前 linktype 的服务器确认快照即时绘制；这里后台拉取最新 list 做最终校正。
      hydratePendingAddsIntoState()
      // 本次连接恢复必须以刚返回的 list 为准，不能被旧的本地保护窗口拦截。
      cloudGuardUntil.value[restoredMode] = 0

      // 先只加载 getLastEq 指定的模式，严格使用该模式 list.lastPreset 恢复标签。
      // 在这一步完成前不恢复任何待确认写请求，保证网络请求顺序就是 getLastEq -> 当前模式 list。
      const restoredListLoaded = await loadFromRemote(restoredMode, true)
      if (requestGeneration !== sourceGeneration.value || !isLoggedIn()) return
      if (!restoredListLoaded) {
        console.warn(`[EQ] ${restoredMode} list 未成功返回，保留上一份服务器确认快照，等待下次后台校正。`)
        return
      }

      activeMode.value = restoredMode
      const state = getModeState(restoredMode)
      const selected = presetsForMode(restoredMode).find(preset => preset.id === state.currentPreset)
      ;[gameEq.value, musicEq.value, movieEq.value].forEach(item => { item.enabled = false })
      if (selected) {
        // 模式/标签来自服务器；开关只采用当前连接 0x80。
        const hardwareEnabled = latestHardwareEqGeneration === requestGeneration
          ? latestHardwareEqEnabled === true
          : false
        state.enabled = hardwareEnabled
        appliedMode.value = hardwareEnabled ? restoredMode : null
        appliedPresetId.value = hardwareEnabled ? selected.id : null
        // 只有当前连接 0x80 开关已拿到后才补发：开=>最后标签完整 EQ；关=>14 段全 0dB。
        void syncRestoredEqToHardware(restoredMode)
      } else {
        appliedMode.value = null
        appliedPresetId.value = null
      }
      writeCloudContextSnapshot(selected ? restoredMode : null)
      writeCloudModeSnapshot(restoredMode, { confirmActiveMode: true })
      sceneRestoreReady.value = true

      // 当前模式恢复完成后再恢复待确认删除，并补拉另外两套列表。
      resumePendingDeletes()
      const otherModes = (['game', 'music', 'movie'] as EqMode[]).filter(mode => mode !== restoredMode)
      void Promise.all(otherModes.map(mode => loadFromRemote(mode, true)))
    } catch (error) {
      if (requestGeneration !== sourceGeneration.value || !isLoggedIn()) return
      console.error('[EQ] 恢复登录态最后一次 EQ 失败:', error)
      // 网络异常时保留上一份服务器确认快照；仍然不会采用硬件 0x80 或另一连接方式的数据。
    } finally {
      // 只允许当前最新账号/连接上下文结束首屏等待；旧请求返回不能提前放开新上下文。
      if (requestGeneration === sourceGeneration.value) displayReady.value = true
    }
  }

  async function waitForDisplayReady(): Promise<void> {
    // 保留兼容导出；EQ 页面不再等待网络恢复链路，也不再整页 loading。
    return
  }

  function authIdentityFromSourceKey(key: string): string {
    if (!key) return ''
    if (key.startsWith('guest:')) return 'guest'
    if (!key.startsWith('user:')) return ''
    const parts = key.split(':')
    return parts.length >= 2 ? `user:${parts[1]}` : 'user'
  }

  function handleSourceContextChange(): void {
    const connection = deviceStore.connectionType || hidManager.getConnectionType() || 'unknown'
    const connected = !!deviceStore.isDeviceConnected
    const nextKey = isLoggedIn()
      ? `user:${getUuid()}:${connection}:${connected ? 'connected' : 'disconnected'}`
      : `guest:${connection}:${connected ? 'connected' : 'disconnected'}`

    if (nextKey === sourceContextKey) return
    const previousKey = sourceContextKey
    const previousAuthIdentity = authIdentityFromSourceKey(previousKey)
    const nextAuthIdentity = isLoggedIn() ? `user:${getUuid()}` : 'guest'
    const authContextChanged = !!previousAuthIdentity && previousAuthIdentity !== nextAuthIdentity

    sourceContextKey = nextKey
    sourceGeneration.value += 1
    const requestGeneration = sourceGeneration.value

    if (authContextChanged) {
      // 登录/退出账号不会重新插拔设备，但硬件会保留“最后一次下发”的 EQ 曲线。
      // 因此账号边界变化必须强制视为一次新的硬件 EQ 同步，不能沿用上一身份的去重签名。
      guestCurveRestoreGeneration = -1
      loggedInCurveRestoreSignature = ''
      console.log('[EQ] 账号状态切换，强制同步硬件 EQ:', {
        from: previousAuthIdentity,
        to: nextAuthIdentity,
        connection,
      })
    }

    // 不再让路由/页面等待网络请求。刷新、拔出、关机、回联都立即保持可见页面。
    displayReady.value = true
    sceneRestoreReady.value = false

    if (isLoggedIn()) {
      const sameUserPrefix = `user:${getUuid()}:`

      if (!connected || connection === 'unknown') {
        // 运行中断联/关机：保留当前已经显示的 EQ，不清空、不转圈。
        // 浏览器刚刷新时还没识别出连接方式，则先用上一次连接方式的服务器确认快照即时绘制。
        if (!previousKey || !previousKey.startsWith(sameUserPrefix)) {
          resetModeData()
          const rememberedLinktype = readRememberedLinktype()
          if (rememberedLinktype) hydrateCloudSnapshot(rememberedLinktype)
        }
        return
      }

      // 已识别 USB / 2.4G：先同步恢复这一套 linktype 的本地“服务器确认快照”，
      // 页面立即有正确的上一状态；getLastEq -> list 只在后台校正，不再阻塞首屏。
      const linktype = getLinktype()
      rememberLinktype(linktype)
      resetModeData()
      // 登录成功本身也必须触发一次硬件同步：设备不会因为账号变化而重新上报初始化包。
      // 这里直接复用最近一次 0x80 缓存取得开关；模式/标签仍只由账号快照/getLastEq + list 决定。
      syncLatestHardwareForCurrentContext()
      loadPendingDeletes()
      loadPendingAdds()
      const hydrated = hydrateCloudSnapshot(linktype)
      hydratePendingAddsIntoState()

      // 回联优先使用当前账号 + 当前 linktype 的“服务器确认快照”快速恢复。
      // 0x80 已经到达时这里会立即排入四包 EQ；若 0x80 尚未到达，后续 syncHardwareStatus 会自动触发。
      // getLastEq + 当前模式 list 仍在后台执行，若服务器最终数据变化，签名变化后会再补发一次最终曲线。
      if (hydrated && loggedInRestoreResolved && loggedInRestoredMode && !suppressLoggedInAutoSelection) {
        queueLoggedInCurveRestore(loggedInRestoredMode)
      }

      void restoreLoggedInContext(requestGeneration)
      return
    }

    // 未登录仍完全以当前硬件 0x80 为准。
    // 退出登录时 resetModeData 会先清掉账号自定义数据并恢复游客内置默认标签；
    // 随后用最近一次 0x80 的开关/模式/标签重新映射，并强制补发一次游客曲线：
    // 开=>游客对应默认标签完整 EQ，关=>14 段全 0dB。这样账号曲线不会继续残留在硬件。
    resetModeData()
    syncLatestHardwareForCurrentContext()
    sceneRestoreReady.value = true
  }

  function loadFromStorage(): void {
    // 刷新初期连接方式可能尚未识别；优先用上一次实际连接方式的服务器确认快照即时绘制。
    if (!isLoggedIn()) return
    const connection = deviceStore.connectionType || hidManager.getConnectionType() || 'unknown'
    const linktype = connection === '2.4G' ? '0' : connection === 'USB' ? '1' : readRememberedLinktype()
    if (linktype) hydrateCloudSnapshot(linktype)
  }

  function saveToStorage(): void {
    // 仅保留轻量 UI 会话信息，不把当前标签/曲线写成云端真值。
    // 服务器标签/曲线快照只会由 writeCloudModeSnapshot 在远端确认后写入。
    try {
      localStorage.setItem('eqSettings', JSON.stringify({ activeMode: activeMode.value }))
    } catch (error) {
      console.warn('[EQ] 保存 UI 会话状态失败:', error)
    }
  }

  watch(
    () => [
      userStore.isLoggedIn,
      userStore.uuid,
      deviceStore.connectionType,
      deviceStore.isDeviceConnected,
    ] as const,
    () => {
      handleSourceContextChange()
    },
    { immediate: true, flush: 'sync' },
  )


  return {
    gameEq,
    musicEq,
    movieEq,
    activeMode,
    appliedMode,
    appliedPresetId,
    remoteLoaded,
    displayReady,
    waitForDeviceQueue,
    sceneRestoreReady,
    currentModeState,
    currentSettings,
    allPresets,
    currentPreset,
    currentPresetData,
    isSystemPreset,
    canAddMoreCustom,
    isEnabled,
    currentMode,
    slidersInteractive,
    setActiveMode,
    switchMode,
    setEqEnabled,
    selectPreset,
    applyPreset, // 场景功能复用已有EQ下发，可选择不写账号最后标签
    updateEqGain,
    updateEqFrequency,
    applyCurrentEq,
    createCustomPresetDraft,
    finalizeCustomPresetDraft,
    isDraftPreset,
    addCustomPreset,
    deleteCustomPreset,
    renameCustomPreset,
    resetCurrentPreset,
    pinPreset,
    loadFromRemote,
    loadAllFromRemote,
    syncHardwareStatus,
    waitForDisplayReady,
    loadFromStorage,
    saveToStorage,
  }
})
